import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, GeoJSON, TileLayer } from "react-leaflet";
import { UFs } from "../../util/UFs";
import "leaflet/dist/leaflet.css";

const GEOJSON_URL =
  "https://raw.githubusercontent.com/codeforgermany/click_that_hood/main/public/data/brazil-states.geojson";

function normalizeUf(raw) {
  if (raw === undefined || raw === null || raw === "") {
    return null;
  }
  const s = String(raw).trim();
  if (s.length === 2) {
    return s.toUpperCase();
  }
  const direct = UFs[s];
  if (direct) {
    return direct;
  }
  const lower = s.toLowerCase();
  for (const [name, sigla] of Object.entries(UFs)) {
    if (name.toLowerCase() === lower) {
      return sigla;
    }
  }
  return null;
}

export function aggregateOriginUfCounts(freightDocs) {
  const counts = {};
  freightDocs.forEach((doc) => {
    const uf = normalizeUf(doc.data().firstDelivery?.uf);
    if (!uf) {
      return;
    }
    counts[uf] = (counts[uf] || 0) + 1;
  });
  return counts;
}

export function aggregateDestinationUfCounts(freightDocs) {
  const counts = {};
  freightDocs.forEach((doc) => {
    const uf = normalizeUf(doc.data().lastDelivery?.uf);
    if (!uf) {
      return;
    }
    counts[uf] = (counts[uf] || 0) + 1;
  });
  return counts;
}

function fillColorForCount(count, max, palette = "blue") {
  if (!count || max <= 0) {
    return "#eceff1";
  }
  const t = Math.min(1, count / max);
  const [c0, c1] =
    palette === "red"
      ? [
          [255, 235, 238],
          [183, 28, 28],
        ]
      : [
          [227, 242, 253],
          [13, 71, 161],
        ];
  const r = Math.round(c0[0] + (c1[0] - c0[0]) * t);
  const g = Math.round(c0[1] + (c1[1] - c0[1]) * t);
  const b = Math.round(c0[2] + (c1[2] - c0[2]) * t);
  return `rgb(${r},${g},${b})`;
}

/**
 * Mapa do Brasil com estados pintados conforme quantidade de fretes por UF.
 */
export default function BrazilFreightOriginMap({ countsByUf = {}, palette = "blue" }) {
  const [geoData, setGeoData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(GEOJSON_URL)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((json) => {
        if (!cancelled) {
          setGeoData(json);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err.message || "Erro ao carregar mapa");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const maxCount = useMemo(() => {
    return Math.max(0, ...Object.values(countsByUf));
  }, [countsByUf]);

  if (loadError) {
    return (
      <p style={{ color: "#c62828", margin: 0 }}>
        Não foi possível carregar o mapa do Brasil ({loadError}).
      </p>
    );
  }

  if (!geoData) {
    return (
      <p style={{ color: "#666", margin: 0 }}>Carregando mapa…</p>
    );
  }

  return (
    <div style={{ width: "100%", height: 380, borderRadius: 8, overflow: "hidden" }}>
      <MapContainer
        center={[-15.5, -55]}
        zoom={4}
        minZoom={3}
        maxZoom={10}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%", background: "#f5f7fa" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
        />
        <GeoJSON
          key={JSON.stringify(countsByUf)}
          data={geoData}
          style={(feature) => {
            const sigla = feature?.properties?.sigla;
            const n = sigla ? countsByUf[sigla] || 0 : 0;
            return {
              fillColor: fillColorForCount(n, maxCount, palette),
              color: "#90a4ae",
              weight: 1,
              fillOpacity: n ? 0.88 : 0.55,
            };
          }}
          onEachFeature={(feature, layer) => {
            const sigla = feature?.properties?.sigla;
            const name = feature?.properties?.name || sigla;
            const n = sigla ? countsByUf[sigla] || 0 : 0;
            layer.bindTooltip(
              `${name} (${sigla || "—"}): ${n} frete${n === 1 ? "" : "s"}`,
              { sticky: true }
            );
          }}
        />
      </MapContainer>
    </div>
  );
}
